import { useMobile } from "../../hooks/useMobile";
import type { ILenderResponseBenefit } from "../../types/lenderResponseBenefit";
import { cn } from "../../utils";
import { Text } from "../Text";
import { getBenefits } from "./getBenefits";

interface IBidBenefits {
  benefits: ILenderResponseBenefit[];
  /** Omitted in the condensed variant, which has no heading. */
  title?: string;
  /** Compact presentation: display names instead of explanations, no heading. */
  isCondensedVersion?: boolean;
  /** Uncapped when unset: the customer offer card passes 2, the lender none. */
  maxItems?: number;
  className?: string;
}

export const BidBenefits = ({
  benefits,
  title,
  isCondensedVersion,
  maxItems,
  className,
}: IBidBenefits) => {
  const isMobile = useMobile();
  const allBenefits = getBenefits(benefits);
  // The slice trusts core-service's ordering and never re-sorts: getBenefits only
  // attaches icons, preserving the order core-service sent, so this cap keeps
  // whatever arrived first.
  //
  // That is safe because of a core-service invariant: it builds the list in
  // _BID_TYPE_BENEFITS_MAP order, and at most ONE type (NO_GUARANTOR_REQUIRED) is
  // ranked above EU_LOAN in any bid type — so EU_LOAN can never land later than
  // 2nd and always survives maxItems={2}. The invariant is "at most one type
  // outranks it", not "it ranks high": the rendered list holds only the benefits
  // that apply, so a map with two types above EU_LOAN would still read as
  // high-ranking while letting it fall out of view. core-service owns and tests
  // this (KREA-4499).
  const relevantBenefits =
    maxItems === undefined ? allBenefits : allBenefits.slice(0, maxItems);

  const getBenefitText = (benefit: ILenderResponseBenefit) => {
    if (isCondensedVersion) return benefit.displayName;
    if (benefit.explanation) return benefit.explanation;

    return benefit.displayName;
  };

  return relevantBenefits.length > 0 ? (
    <div
      className={cn(
        {
          "min-w-[200px]": isCondensedVersion,
        },
        className,
      )}
    >
      {title && !isCondensedVersion && !isMobile && (
        <Text className="mb-4 text-subsection">{title}</Text>
      )}

      <ul
        className={cn("list-none", {
          "border border-solid border-primary-150 bg-white px-2 py-3 rounded-xl":
            isMobile,
          "m-0 p-0": !isMobile,
        })}
      >
        {relevantBenefits.map(
          (benefit) =>
            benefit.icon &&
            benefit.displayName && (
              // core-service dedupes benefits by type, so it is a stable key.
              <li
                key={benefit.type}
                className={cn("flex items-center", {
                  "my-3": !isCondensedVersion && !isMobile,
                })}
              >
                <div className="mr-2 flex h-[18px] w-[18px] shrink-0 items-center justify-center">
                  <benefit.icon className="text-primary" size={16} />
                </div>

                <Text
                  className={cn(isMobile ? "text-helper" : "text-sm", "mb-0")}
                >
                  {getBenefitText(benefit)}
                </Text>
              </li>
            ),
        )}
      </ul>
    </div>
  ) : null;
};
